import React from 'react';
import {QuickSettingsForm, FormFieldConfig} from './index';
import * as yup from 'yup';
import useData from '@/utils/useData';

interface DynamicFieldConfig {
    name: string;
    type: string;
    label: string;
    placeholder?: string;
    required: boolean;
    validation?: any;
    props?: any;
    options?: Array<{ value: any; label: string }>;
    helpText?: string;
    dependsOn?: string;
}

interface DynamicFieldsetConfig {
    title: string;
    description: string;
    fields: DynamicFieldConfig[];
    submitText: string;
    validation: Record<string, string>;
}

interface DynamicFormRendererProps {
    fieldsetUrl: string;
    entityId?: number;
    crudConfig: {
        resourceUrl: string;
        storeActionPath: string;
        successMessageKey: string;
        entityName: string;
    };
    initialValues?: Record<string, any>;
    onSave?: () => void;
    customSuccessHandling?: (response: any, actionName: string) => void;
    customErrorHandling?: (error: any, formik: any) => boolean;
    additionalStoreActions?: Array<{
        path: string;
        value: any;
    }>;
}

const DynamicFormRenderer: React.FC<DynamicFormRendererProps> = ({
    fieldsetUrl,
    entityId,
    crudConfig,
    initialValues = {},
    onSave,
    customSuccessHandling,
    customErrorHandling,
    additionalStoreActions,
}) => {
    const [fieldsetConfig, loading, error] = useData<DynamicFieldsetConfig>(fieldsetUrl, 'dynamicFieldset');

    if (loading) {
        return <div>Loading form configuration...</div>;
    }

    if (error || !fieldsetConfig) {
        return <div>Error loading form configuration: {(error as any)?.message || 'Unknown error'}</div>;
    }

    // Convert dynamic field configs to FormFieldConfig format
    const convertToFormFields = (dynamicFields: DynamicFieldConfig[]): FormFieldConfig[] => {
        return dynamicFields.map((field) => {
            const formField: FormFieldConfig = {
                name: field.name,
                label: field.label,
                type: field.type as any,
                required: field.required,
                validation: createValidationSchema(field),
                placeholder: field.placeholder,
            };

            // Add type-specific props directly to formField
            switch (field.type) {
                case 'select':
                    formField.options = field.options || [];
                    break;
                case 'multiSelect':
                    formField.options = field.options || [];
                    formField.isSearchable = field.props?.isSearchable ?? true;
                    formField.closeMenuOnSelect = field.props?.closeMenuOnSelect ?? false;
                    formField.enableSelectAll = field.props?.enableSelectAll ?? false;
                    formField.maxSelectedItems = field.props?.maxSelectedItems;
                    break;
                case 'asyncSelect':
                    formField.loadOptions = createAsyncLoader(field.props?.loadOptionsUrl);
                    formField.isMulti = false;
                    formField.isSearchable = field.props?.isSearchable ?? true;
                    formField.debounceMs = field.props?.debounceMs ?? 300;
                    break;
                case 'richText':
                    formField.minHeight = field.props?.minHeight ?? '150px';
                    formField.customHeight = field.props?.customHeight;
                    break;
                case 'text':
                    // For textarea, we'll handle this in the QuickSettingsForm
                    break;
                case 'switch':
                    formField.size = field.props?.size;
                    formField.variant = field.props?.variant ?? 'switch';
                    break;
                case 'range':
                    formField.min = field.validation?.min ?? 0;
                    formField.max = field.validation?.max ?? 100;
                    formField.step = field.props?.step ?? 1;
                    formField.showValue = field.props?.showValue ?? true;
                    formField.showMinMax = field.props?.showMinMax ?? false;
                    formField.unit = field.props?.unit;
                    formField.marks = field.props?.marks;
                    break;
                case 'date':
                    formField.dateFormat = field.props?.dateFormat ?? 'dd/MM/yyyy';
                    formField.isClearable = field.props?.isClearable ?? true;
                    break;
                case 'fileUpload':
                    formField.multiple = field.props?.multiple ?? false;
                    formField.accept = field.props?.accept;
                    formField.maxSize = field.props?.maxSize ?? 10 * 1024 * 1024; // 10MB default
                    formField.maxFiles = field.props?.maxFiles ?? 1;
                    break;
            }

            return formField;
        });
    };

    // Create validation schema from field configuration
    const createValidationSchema = (field: DynamicFieldConfig): yup.Schema<any> => {
        let schema: yup.Schema<any>;

        switch (field.validation?.type) {
            case 'string':
                schema = yup.string();
                if (field.validation.minLength) {
                    schema = (schema as yup.StringSchema).min(field.validation.minLength);
                }
                if (field.validation.maxLength) {
                    schema = (schema as yup.StringSchema).max(field.validation.maxLength);
                }
                break;
            case 'number':
                schema = yup.number();
                if (field.validation.min !== undefined) {
                    schema = (schema as yup.NumberSchema).min(field.validation.min);
                }
                if (field.validation.max !== undefined) {
                    schema = (schema as yup.NumberSchema).max(field.validation.max);
                }
                break;
            case 'email':
                schema = yup.string().email('Invalid email format');
                break;
            case 'url':
                schema = yup.string().url('Invalid URL format');
                break;
            case 'array':
                schema = yup.array();
                if (field.validation.minItems) {
                    schema = (schema as yup.ArraySchema<any, any>).min(field.validation.minItems);
                }
                if (field.validation.maxItems) {
                    schema = (schema as yup.ArraySchema<any, any>).max(field.validation.maxItems);
                }
                if (field.validation.itemType === 'email') {
                    schema = (schema as yup.ArraySchema<any, any>).of(
                        yup.string().email('Invalid email format')
                    );
                }
                break;
            default:
                schema = yup.mixed();
        }

        if (field.required) {
            schema = schema.required(`${field.label} is required`);
        }

        return schema;
    };

    // Create async loader function for AsyncSelect fields
    const createAsyncLoader = (url?: string) => {
        if (!url) {return undefined;}

        return async (inputValue: string) => {
            try {
                const response = await fetch(`${url}?search=${encodeURIComponent(inputValue)}`);
                const data = await response.json();
                return data.results || data.items || data;
            } catch (error) {
                console.error('Error loading async options:', error);
                return [];
            }
        };
    };

    // Build initial values from fieldset config
    const buildInitialValues = (): Record<string, any> => {
        const values: Record<string, any> = {...initialValues};

        fieldsetConfig.fields.forEach((field) => {
            if (!(field.name in values)) {
                // Set default values based on field type
                switch (field.type) {
                    case 'multiSelect':
                    case 'array':
                        values[field.name] = [];
                        break;
                    case 'switch':
                        values[field.name] = false;
                        break;
                    case 'number':
                        values[field.name] = field.validation?.min ?? 0;
                        break;
                    default:
                        values[field.name] = '';
                }
            }
        });

        return values;
    };

    const formFields = convertToFormFields(fieldsetConfig.fields);
    const formInitialValues = buildInitialValues();

    return (
        <div>
            {fieldsetConfig.title && (
                <div className="mb-3">
                    <h5>{fieldsetConfig.title}</h5>
                    {fieldsetConfig.description && (
                        <p className="text-muted small">{fieldsetConfig.description}</p>
                    )}
                </div>
            )}
            
            <QuickSettingsForm
                entityId={entityId}
                crudConfig={crudConfig}
                fields={formFields}
                initialValues={formInitialValues}
                onSave={onSave}
                customSuccessHandling={customSuccessHandling}
                customErrorHandling={customErrorHandling}
                additionalStoreActions={additionalStoreActions}
            />
        </div>
    );
};

export default DynamicFormRenderer;
